// Package Imports
import { create } from 'zustand'

// Core Imports
import { api } from '@/core/lib/api'
import { queryClient } from '@/core/lib/query-client'
import { notify } from '@/core/lib/ui-bus'

// Module Imports
import type { TThemeName, TUser } from '@/modules/auth/auth.types'

type TToggleOrigin = { clientX: number; clientY: number }

type TThemeStore = {
  theme: TThemeName
  hydrated: boolean
  /** Sync local theme from the auth session. Does not hit the API. */
  hydrate: (theme: TThemeName) => void
  /** Reset to default `klasik` and clear hydrated flag. Used on sign-out. */
  reset: () => void
  /** Set theme + persist to backend. Optimistic — reverts on failure.
   * Skips the PUT when the user is unauthenticated; just updates local state. */
  setTheme: (theme: TThemeName) => Promise<void>
  /** Flip theme. Wraps `setTheme` with the view-transition circle reveal. */
  toggleTheme: (origin?: TToggleOrigin) => void
}

const isAuthenticated = (): boolean =>
  queryClient.getQueryData<TUser | undefined>(['authUser']) != null

const applyDocumentTheme = (theme: TThemeName) => {
  if (typeof document === 'undefined') return
  document.documentElement.dataset.theme = theme
}

const updateAuthUserCache = (theme: TThemeName) => {
  queryClient.setQueryData<TUser | undefined>(['authUser'], (prev) =>
    prev ? { ...prev, theme } : prev
  )
}

const persistTheme = async (theme: TThemeName): Promise<void> => {
  await api.put('/admin/profile/theme', { theme })
}

export const useThemeStore = create<TThemeStore>((set, get) => ({
  theme: 'klasik',
  hydrated: false,

  hydrate: (theme) => {
    applyDocumentTheme(theme)
    set({ theme, hydrated: true })
  },

  reset: () => {
    applyDocumentTheme('klasik')
    set({ theme: 'klasik', hydrated: false })
  },

  setTheme: async (next) => {
    const prev = get().theme
    if (prev === next) return

    applyDocumentTheme(next)
    set({ theme: next })

    if (!isAuthenticated()) return

    updateAuthUserCache(next)

    try {
      await persistTheme(next)
    } catch {
      applyDocumentTheme(prev)
      set({ theme: prev })
      updateAuthUserCache(prev)
      notify.error('Görünüm tercihiniz kaydedilemedi.')
    }
  },

  toggleTheme: (origin) => {
    const next: TThemeName = get().theme === 'klasik' ? 'canli' : 'klasik'

    const doc = document as Document & {
      startViewTransition?: (cb: () => void) => {
        ready: Promise<void>
        finished: Promise<void>
      }
    }

    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (!doc.startViewTransition || prefersReduced) {
      void get().setTheme(next)
      return
    }

    const x = origin?.clientX ?? window.innerWidth / 2
    const y = origin?.clientY ?? window.innerHeight / 2
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    )

    const transition = doc.startViewTransition(() => {
      void get().setTheme(next)
    })

    transition.ready
      .then(() => {
        document.documentElement.animate(
          {
            clipPath: [
              `circle(0px at ${x}px ${y}px)`,
              `circle(${endRadius}px at ${x}px ${y}px)`,
            ],
          },
          {
            duration: 650,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
            pseudoElement: '::view-transition-new(root)',
          }
        )
      })
      .catch((err) => {
        console.error('[themeStore.toggleTheme.transition]', err)
      })
  },
}))
